import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import MicrofoneIcon from '../../../../app/assets/microfone-white-icon.png';

const pulseAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0.5;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
`;

const RecordButton = styled.button<{ isRecording: boolean }>`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background-color: ${({ isRecording }) => (isRecording ? 'rgb(156, 41, 41)' : 'rgb(220, 57, 57)')};
  cursor: pointer;
  transition: background-color 0.3s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:focus {
    outline: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: -32px;
    left: -32px;
    width: 128px;
    height: 128px;
    border-radius: 50%;
    border: 2px solid #ff0000;
    opacity: 0;
    animation: ${({ isRecording }) => (isRecording ? css`${pulseAnimation} 1s infinite` : 'none')};
  }
`;

const MicrophoneIcon = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
`;

interface AudioContentRecorderProps {
  onOpenRecorderModal: React.Dispatch<React.SetStateAction<boolean>>;
  onAudioRecordedUrl: React.Dispatch<React.SetStateAction<string | null>>;
}

const AudioContentRecorder: React.FC<AudioContentRecorderProps> = ({ onOpenRecorderModal, onAudioRecordedUrl }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const mediaStream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);
  const isProcessing = useRef(false);

  const startRecording = async () => {
    if (isProcessing.current) return;
    
    isProcessing.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
        }
      });
      
      mediaStream.current = stream;
      
      const options = { 
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      };
      
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/mp4';
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/webm';
      }
      
      mediaRecorder.current = new MediaRecorder(stream, options);
      
      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };
      
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { 
          type: mediaRecorder.current?.mimeType || 'audio/webm' 
        });
        
        setAudioBlob(blob);
        const audioUrl = URL.createObjectURL(blob);
        onAudioRecordedUrl(audioUrl);
        
        if (mediaStream.current) {
          mediaStream.current.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
          });
          mediaStream.current = null;
        }
        
        chunks.current = [];
        mediaRecorder.current = null;
        setIsRecording(false);
        isProcessing.current = false;
        
        onOpenRecorderModal(true);
      };
      
      mediaRecorder.current.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        isProcessing.current = false;
        setIsRecording(false);
      };

      setIsRecording(true);
      await new Promise<void>(resolve => setTimeout(resolve, 250));

      if (!mediaStream.current) return;

      mediaRecorder.current.start(100);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      isProcessing.current = false;
      setIsRecording(false);
      
      alert('Permissão de microfone negada. Por favor, permita o acesso ao microfone nas configurações do seu navegador.');
    }
  };

  const stopRecording = () => {
    if (isProcessing.current && mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleTouch = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.touches.length > 1) return;
    
    handleToggleRecording();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleToggleRecording();
  };

  useEffect(() => {
    return () => {
      if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
        mediaRecorder.current.stop();
      }
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="audio-recorder">
      <RecordButton 
        className={`record-button ${isRecording ? 'recording' : ''}`}
        isRecording={isRecording} 
        onClick={handleClick}
        onTouchStart={handleTouch}
        onTouchEnd={(e) => e.preventDefault()}
      >
        <MicrophoneIcon src={MicrofoneIcon} alt="Microphone Icon" />
      </RecordButton>
      <audio controls ref={audioRef} style={{ display: 'none' }} />
      
      {isRecording && (
        <div style={{
          position: 'fixed',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '20px',
          zIndex: 1000,
          fontSize: '14px'
        }}>
          Gravando... Clique para parar
        </div>
      )}
    </div>
  );
};

export default AudioContentRecorder;